import moment from 'moment';
import { comparePeriodsEqual, detectLineCompareGranularity, TREND_GRANULARITY, resolveComparePeriodPrimaryYear, shiftComparePeriodByOffset } from './statisticsComparePresets';
import { deriveAnchorMonthIndices, deriveAnchorQuarterIndices, isDualFullYearQuarterAnchor, PERIOD_QUICK_PICK_AXIS } from './periodQuickPickStaging';
import { TREND_MODE, TREND_COMPARE_AXIS_UNIT, TREND_MONTHLY_YEARS_MIN, TREND_MONTHLY_YEARS_MAX, normalizeLineCompareMonthlyYears } from './trendMonthlyConstants';
import { PERIOD_YOY_GRANULARITY } from './statisticsPeriodYoyHelpers';
import { buildCalendarMonthRange, buildCalendarQuarterRange } from './statisticsPeriodQuickPickUtils';
import { resolveOverviewPeriodUnit } from './overviewPeriodUnit';

export { TREND_MODE, TREND_COMPARE_AXIS_UNIT } from './trendMonthlyConstants';
export { normalizeLineCompareMonthlyYears } from './trendMonthlyConstants';

const ALL_MONTH_INDICES = Array.from({ length: 12 }, (_, index) => index);
const ALL_QUARTER_INDICES = Array.from({ length: 4 }, (_, index) => index);

const extractFullCalendarYear = (startDate, endDate) => {
	if (!startDate || !endDate) return null;
	if (detectLineCompareGranularity(startDate, endDate) !== TREND_GRANULARITY.YEAR) return null;
	const year = moment(startDate).year();
	return Number.isFinite(year) ? year : null;
};

/** 当年/当月/当季：整段一个轴点，不拆子月 */
const expandOverviewPeriodUnitToSingleSegment = (anchor) => {
	const unit = resolveOverviewPeriodUnit(anchor?.startDate, anchor?.endDate, anchor);
	if (!unit || !anchor?.startDate) return null;
	const year = moment(anchor.startDate).year();
	if (!Number.isFinite(year)) return null;
	if (unit === 'month') {
		const range = buildCalendarMonthRange(year, moment(anchor.startDate).month());
		return [
			{
				startDate: range.startDate,
				endDate: range.endDate,
				label: range.label,
				segmentKind: 'anchor',
			},
		];
	}
	if (unit === 'quarter') {
		const range = buildCalendarQuarterRange(year, Math.floor(moment(anchor.startDate).month() / 3));
		return [
			{
				startDate: range.startDate,
				endDate: range.endDate,
				label: range.label,
				segmentKind: 'anchor',
			},
		];
	}
	/** 当年：不拆段，由 skipAnchorExpand / compareAnchor 整段一点 */
	if (unit === 'year') return null;
	return null;
};

const resolveAnchorPeriodSpec = (anchor) => {
	if (!anchor?.startDate || !anchor?.endDate) return null;
	const anchorYear = moment(anchor.startDate).year();
	if (!Number.isFinite(anchorYear)) return null;

	const useQuarterAxis = anchor.periodAxis === 'quarter' && Array.isArray(anchor.quarters) && anchor.quarters.length > 0;

	if (useQuarterAxis) {
		const indices = anchor.quarters.length === ALL_QUARTER_INDICES.length ? ALL_QUARTER_INDICES : anchor.quarters;
		return {
			granularity: PERIOD_YOY_GRANULARITY.QUARTER,
			indices,
		};
	}

	if (anchor.months?.length) {
		const indices = anchor.months.length === ALL_MONTH_INDICES.length ? ALL_MONTH_INDICES : anchor.months;
		return {
			granularity: PERIOD_YOY_GRANULARITY.MONTH,
			indices,
		};
	}

	// 无显式月/季列表时：先尝试拆成各月，避免整季区间被默认汇总为一点
	const monthIndices = deriveAnchorMonthIndices(anchor.startDate, anchor.endDate, anchorYear);
	if (monthIndices?.length) {
		const indices = monthIndices.length === ALL_MONTH_INDICES.length ? ALL_MONTH_INDICES : monthIndices;
		return {
			granularity: PERIOD_YOY_GRANULARITY.MONTH,
			indices,
		};
	}

	const quarterIndices = deriveAnchorQuarterIndices(anchor.startDate, anchor.endDate, anchorYear);
	if (quarterIndices?.length) {
		const indices = quarterIndices.length === ALL_QUARTER_INDICES.length ? ALL_QUARTER_INDICES : quarterIndices;
		return {
			granularity: PERIOD_YOY_GRANULARITY.QUARTER,
			indices,
		};
	}

	return null;
};

const resolveOrderRangePeriodMonthIndices = (period = {}, anchor = {}) => {
	const year = moment(period.startDate).year();
	if (!Number.isFinite(year)) return [];

	const yearEntry = anchor.yearPeriods?.[year] ?? anchor.yearPeriods?.[String(year)];
	if (yearEntry?.months?.length) {
		return [...new Set(yearEntry.months)].sort((a, b) => a - b);
	}

	if (period.orderCreatedMonths?.length) {
		return period.orderCreatedMonths.map((month) => Number(month) - 1).filter((month) => Number.isFinite(month) && month >= 0 && month <= 11);
	}

	if (period.months?.length) {
		return [...new Set(period.months)].sort((a, b) => a - b);
	}

	if (period.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER && period.quarters?.length) {
		const monthSet = new Set();
		for (const quarter of period.quarters) {
			for (let month = quarter * 3; month < quarter * 3 + 3; month += 1) {
				monthSet.add(month);
			}
		}
		return [...monthSet].sort((a, b) => a - b);
	}

	const derived = deriveAnchorMonthIndices(period.startDate, period.endDate, year);
	return derived?.length ? [...derived].sort((a, b) => a - b) : [];
};

/** 顶栏多年/各年细选不一致：不宜用单一 periodAxis 驱动图表 */
export const isOrderRangeChartAxisAmbiguous = (anchor) => {
	const yearPeriods = anchor?.yearPeriods;
	if (!yearPeriods || typeof yearPeriods !== 'object') return false;

	const entries = Object.entries(yearPeriods).filter(([, entry]) => entry?.months?.length);
	if (entries.length < 2) return false;

	const axes = new Set(entries.map(([, entry]) => (entry.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER ? PERIOD_QUICK_PICK_AXIS.QUARTER : PERIOD_QUICK_PICK_AXIS.MONTH)));
	if (axes.size > 1) return true;

	const monthSignatures = entries.map(([, entry]) => [...entry.months].sort((a, b) => a - b).join(','));
	return new Set(monthSignatures).size > 1;
};

/** 订单范围与图表轴解耦：按 periods / yearPeriods 统一拆成「按月各点」 */
export const shouldUseOrderRangeMonthChartSegments = (anchor) => {
	if (!anchor?.startDate || !anchor?.endDate) return false;
	if (Array.isArray(anchor.periods) && anchor.periods.length > 1) return true;
	return isOrderRangeChartAxisAmbiguous(anchor);
};

/** 顶栏下单日期 filter periods → 图表基准分段（固定按月） */
export const expandOrderRangeFilterToMonthSegments = (anchor) => {
	if (!anchor?.startDate || !anchor?.endDate) return null;

	const filterPeriods = Array.isArray(anchor.periods) && anchor.periods.length > 0 ? anchor.periods : [{ startDate: anchor.startDate, endDate: anchor.endDate }];

	const segments = [];
	for (const period of filterPeriods) {
		const year = moment(period.startDate).year();
		if (!Number.isFinite(year)) continue;

		const monthIndices = resolveOrderRangePeriodMonthIndices(period, anchor);
		for (const monthIndex of monthIndices) {
			const range = buildCalendarMonthRange(year, monthIndex);
			segments.push({
				startDate: range.startDate,
				endDate: range.endDate,
				label: range.label,
				segmentKind: 'anchor',
			});
		}
	}

	return segments.length ? segments : null;
};

const resolveOrderRangePeriodQuarterIndices = (period = {}) => {
	if (period.periodAxis === PERIOD_QUICK_PICK_AXIS.QUARTER && period.quarters?.length) {
		return [...new Set(period.quarters)].sort((a, b) => a - b);
	}
	const year = moment(period.startDate).year();
	if (!Number.isFinite(year)) return [];
	const derived = deriveAnchorQuarterIndices(period.startDate, period.endDate, year);
	return derived?.length ? [...derived].sort((a, b) => a - b) : [];
};

/** 多段订单范围 → 按季拆点 */
export const expandOrderRangeFilterToQuarterSegments = (anchor) => {
	if (!anchor?.startDate || !anchor?.endDate) return null;

	const filterPeriods =
		Array.isArray(anchor.periods) && anchor.periods.length > 0
			? anchor.periods
			: [{ startDate: anchor.startDate, endDate: anchor.endDate, periodAxis: 'quarter', quarters: anchor.quarters }];

	const segments = [];
	for (const period of filterPeriods) {
		const year = moment(period.startDate).year();
		if (!Number.isFinite(year)) continue;
		const quarterIndices = resolveOrderRangePeriodQuarterIndices(period);
		for (const quarterIndex of quarterIndices) {
			const range = buildCalendarQuarterRange(year, quarterIndex);
			segments.push({
				startDate: range.startDate,
				endDate: range.endDate,
				label: range.label,
				segmentKind: 'anchor',
			});
		}
	}

	return segments.length ? segments : null;
};

/** 锚点按「按月各点 / 按季汇总」拆成各段（非多年折线模式时用） */
export const expandLineCompareAnchorToSegments = (anchor) => {
	if (!anchor?.startDate || !anchor?.endDate) return null;

	if (shouldUseOrderRangeMonthChartSegments(anchor)) {
		if (anchor.periodAxis === 'quarter') {
			return expandOrderRangeFilterToQuarterSegments(anchor);
		}
		return expandOrderRangeFilterToMonthSegments(anchor);
	}

	const year = moment(anchor.startDate).year();
	if (!Number.isFinite(year)) return null;

	/** 显式粒度优先于经营总览「当年/当月/当季整段一点」 */
	if (anchor.periodAxis === 'quarter' && anchor.quarters?.length) {
		return anchor.quarters.map((quarterIndex) => {
			const range = buildCalendarQuarterRange(year, quarterIndex);
			return {
				startDate: range.startDate,
				endDate: range.endDate,
				label: range.label,
				segmentKind: 'anchor',
			};
		});
	}

	if (anchor.periodAxis === 'month' && anchor.months?.length) {
		return anchor.months.map((monthIndex) => {
			const range = buildCalendarMonthRange(year, monthIndex);
			return {
				startDate: range.startDate,
				endDate: range.endDate,
				label: range.label,
				segmentKind: 'anchor',
			};
		});
	}

	const overviewSingle = expandOverviewPeriodUnitToSingleSegment(anchor);
	if (overviewSingle) return overviewSingle;
	if (resolveOverviewPeriodUnit(anchor.startDate, anchor.endDate, anchor) === 'year') return null;

	const derivedMonthIndices = deriveAnchorMonthIndices(anchor.startDate, anchor.endDate, year);
	if (derivedMonthIndices?.length) {
		return derivedMonthIndices.map((monthIndex) => {
			const range = buildCalendarMonthRange(year, monthIndex);
			return {
				startDate: range.startDate,
				endDate: range.endDate,
				label: range.label,
				segmentKind: 'anchor',
			};
		});
	}

	if (detectLineCompareGranularity(anchor.startDate, anchor.endDate) === TREND_GRANULARITY.YEAR) {
		return ALL_QUARTER_INDICES.map((quarterIndex) => {
			const range = buildCalendarQuarterRange(year, quarterIndex);
			return {
				startDate: range.startDate,
				endDate: range.endDate,
				label: range.label,
				segmentKind: 'anchor',
			};
		});
	}

	return null;
};

const collectLineCompareYears = (anchor, customPeriods = []) => {
	if (!anchor?.startDate || !anchor?.endDate) return [];

	const years = new Set();
	const anchorFullYear = extractFullCalendarYear(anchor.startDate, anchor.endDate);
	if (anchorFullYear != null) {
		years.add(anchorFullYear);
	} else {
		const anchorYear = moment(anchor.startDate).year();
		if (Number.isFinite(anchorYear)) years.add(anchorYear);
	}

	for (const period of customPeriods) {
		if (!period?.startDate || !period?.endDate) continue;
		const fullYear = extractFullCalendarYear(period.startDate, period.endDate);
		if (fullYear != null) {
			years.add(fullYear);
			continue;
		}
		const year = moment(period.startDate).year();
		if (Number.isFinite(year)) years.add(year);
	}

	return [...years].sort((a, b) => a - b);
};

const sortedIndicesEqual = (left = [], right = []) => {
	if (left.length !== right.length) return false;
	const a = [...left].sort((x, y) => x - y);
	const b = [...right].sort((x, y) => x - y);
	return a.every((value, index) => value === b[index]);
};

/** 对比段须为锚点按年平移后的同月/同季区间，才进入多年折线模式 */
const customComparePeriodsAlignForYearMonths = (anchor, customPeriods = [], periodSpec = null) => {
	if (!anchor?.startDate || !anchor?.endDate || !customPeriods.length) return true;

	const anchorYear = moment(anchor.startDate).year();
	if (!Number.isFinite(anchorYear)) return false;

	const anchorMonths = periodSpec?.granularity === PERIOD_YOY_GRANULARITY.MONTH ? periodSpec.indices : null;
	const anchorQuarters = periodSpec?.granularity === PERIOD_YOY_GRANULARITY.QUARTER ? periodSpec.indices : null;
	const anchorIsFullYear = extractFullCalendarYear(anchor.startDate, anchor.endDate) != null;

	return customPeriods.every((period) => {
		if (!period?.startDate || !period?.endDate) return false;

		if (anchorIsFullYear && extractFullCalendarYear(period.startDate, period.endDate) != null) {
			return true;
		}

		const offsetYears = moment(period.startDate).year() - anchorYear;
		const shifted = shiftComparePeriodByOffset(anchor.startDate, anchor.endDate, offsetYears);
		if (shifted && comparePeriodsEqual(period, shifted)) return true;

		if (anchorMonths?.length && period.months?.length && sortedIndicesEqual(period.months, anchorMonths)) {
			return true;
		}

		if (anchorQuarters?.length && period.quarters?.length && sortedIndicesEqual(period.quarters, anchorQuarters)) {
			return true;
		}

		return false;
	});
};

/** 有对比段 chip 时，忽略陈旧的 trendMonthlyYears，仅从锚点+对比段推导 */
export const resolveTrendMonthlySpecForQuery = (filters = {}) => {
	if (filters.trendCompareAxisUnit === TREND_COMPARE_AXIS_UNIT.YEAR) return null;

	const storedYears = normalizeLineCompareMonthlyYears(filters.trendMonthlyYears, filters.trendMonthlyYear);
	if (filters.trendMode === TREND_MODE.YEAR_MONTHS && storedYears.length >= TREND_MONTHLY_YEARS_MIN) {
		return resolveLineCompareMonthlySpec(filters);
	}
	if ((filters.customComparePeriods ?? []).length > 0) {
		return resolveLineCompareMonthlySpec({
			...filters,
			trendMode: TREND_MODE.PERIODS,
			trendMonthlyYears: [],
		});
	}
	return resolveLineCompareMonthlySpec(filters);
};

const dedupeCustomComparePeriodsByYear = (periods = []) => {
	const seenYears = new Set();
	const result = [];
	for (let index = periods.length - 1; index >= 0; index -= 1) {
		const period = periods[index];
		const year = resolveComparePeriodPrimaryYear(period);
		if (year == null) {
			result.unshift(period);
			continue;
		}
		if (seenYears.has(year)) continue;
		seenYears.add(year);
		result.unshift(period);
	}
	return result;
};

/** 应用/保存前：将折线对比模式与年份列表与当前对比段对齐 */
export const syncStatisticsLineCompareFilterFields = (filters = {}) => {
	const dedupedPeriods = dedupeCustomComparePeriodsByYear(filters.customComparePeriods ?? []);
	const alignedFilters = dedupedPeriods.length === (filters.customComparePeriods ?? []).length ? filters : { ...filters, customComparePeriods: dedupedPeriods };

	if (alignedFilters.trendCompareAxisUnit === TREND_COMPARE_AXIS_UNIT.YEAR) {
		return {
			...alignedFilters,
			trendMode: TREND_MODE.PERIODS,
			trendMonthlyYears: [],
		};
	}

	const spec = resolveTrendMonthlySpecForQuery(alignedFilters);
	if (!spec?.years?.length) {
		return {
			...alignedFilters,
			trendMode: TREND_MODE.PERIODS,
			trendMonthlyYears: [],
		};
	}
	return {
		...alignedFilters,
		trendMode: TREND_MODE.YEAR_MONTHS,
		trendMonthlyYears: spec.years,
	};
};

/** 折线各月/各季对比：整年对齐，或锚点为多个月/季且含 2+ 年 */
export const resolveLineCompareMonthlySpec = (filters = {}) => {
	if (filters.trendCompareAxisUnit === TREND_COMPARE_AXIS_UNIT.YEAR) return null;

	const forceMonthAxis = filters.trendCompareAxisUnit === TREND_COMPARE_AXIS_UNIT.MONTH;
	const forceQuarterAxis = filters.trendCompareAxisUnit === TREND_COMPARE_AXIS_UNIT.QUARTER;
	if (forceMonthAxis || forceQuarterAxis) {
		let years = collectLineCompareYears(filters.trendAnchor, filters.customComparePeriods ?? []);
		if (years.length === 1) {
			years = [years[0] - 1, years[0]];
		}
		if (years.length < TREND_MONTHLY_YEARS_MIN) return null;
		const cappedYears = years.length > TREND_MONTHLY_YEARS_MAX ? years.slice(years.length - TREND_MONTHLY_YEARS_MAX) : years;
		const periodSpec = resolveAnchorPeriodSpec(filters.trendAnchor);
		if (forceQuarterAxis) {
			const quarters =
				periodSpec?.granularity === PERIOD_YOY_GRANULARITY.QUARTER && periodSpec.indices?.length
					? periodSpec.indices
					: ALL_QUARTER_INDICES;
			return {
				years: cappedYears,
				months: null,
				quarters: quarters.length < ALL_QUARTER_INDICES.length ? quarters : ALL_QUARTER_INDICES,
				granularity: PERIOD_YOY_GRANULARITY.QUARTER,
				anchorYear: cappedYears[cappedYears.length - 1],
			};
		}
		let months = ALL_MONTH_INDICES;
		if (periodSpec?.granularity === PERIOD_YOY_GRANULARITY.MONTH && periodSpec.indices?.length) {
			months = periodSpec.indices;
		} else if (periodSpec?.granularity === PERIOD_YOY_GRANULARITY.QUARTER && periodSpec.indices?.length) {
			months = periodSpec.indices.flatMap((quarter) => [quarter * 3, quarter * 3 + 1, quarter * 3 + 2]);
		}
		return {
			years: cappedYears,
			months: months.length < ALL_MONTH_INDICES.length ? months : ALL_MONTH_INDICES,
			quarters: null,
			granularity: PERIOD_YOY_GRANULARITY.MONTH,
			anchorYear: cappedYears[cappedYears.length - 1],
		};
	}

	const customPeriods = filters.customComparePeriods ?? [];
	const legacyYears = normalizeLineCompareMonthlyYears(filters.trendMonthlyYears, filters.trendMonthlyYear);
	// 已显式 year-months + 合法年份：直接用（勿因仍残留 customComparePeriods / 非整年锚点而退化成分段对比）
	if (filters.trendMode === TREND_MODE.YEAR_MONTHS && legacyYears.length >= TREND_MONTHLY_YEARS_MIN) {
		const periodSpec = resolveAnchorPeriodSpec(filters.trendAnchor);
		const useQuarters = periodSpec?.granularity === PERIOD_YOY_GRANULARITY.QUARTER && periodSpec?.indices?.length;
		const useMonths = !useQuarters && periodSpec?.indices?.length && periodSpec.indices.length < 12;
		return {
			years: legacyYears,
			months: useMonths ? periodSpec.indices : ALL_MONTH_INDICES,
			quarters: useQuarters && periodSpec.indices.length < 4 ? periodSpec.indices : null,
			granularity: useQuarters ? PERIOD_YOY_GRANULARITY.QUARTER : PERIOD_YOY_GRANULARITY.MONTH,
			anchorYear: legacyYears[legacyYears.length - 1],
		};
	}

	const anchor = filters.trendAnchor;
	if (shouldUseOrderRangeMonthChartSegments(anchor)) return null;

	const years = collectLineCompareYears(anchor, customPeriods);
	if (years.length < TREND_MONTHLY_YEARS_MIN) return null;

	const anchorFullYear = extractFullCalendarYear(anchor?.startDate, anchor?.endDate);
	const allFullYearPeriods =
		anchorFullYear != null && customPeriods.length > 0 && customPeriods.every((period) => extractFullCalendarYear(period?.startDate, period?.endDate) != null);

	const periodSpec = resolveAnchorPeriodSpec(anchor);
	const hasExplicitPeriods = Boolean(periodSpec?.indices?.length);

	if (!allFullYearPeriods && !hasExplicitPeriods && !isDualFullYearQuarterAnchor(anchor)) return null;

	const cappedYears = years.length > TREND_MONTHLY_YEARS_MAX ? years.slice(years.length - TREND_MONTHLY_YEARS_MAX) : years;

	if (isDualFullYearQuarterAnchor(anchor) && customPeriods.length > 0) {
		return {
			years: cappedYears,
			months: ALL_MONTH_INDICES,
			quarters: ALL_QUARTER_INDICES,
			dualFullPeriod: true,
			granularity: PERIOD_YOY_GRANULARITY.MONTH,
			anchorYear: cappedYears[cappedYears.length - 1],
		};
	}

	if (allFullYearPeriods && !anchor?.quarters?.length) {
		return {
			years: cappedYears,
			months: ALL_MONTH_INDICES,
			quarters: null,
			granularity: PERIOD_YOY_GRANULARITY.MONTH,
			anchorYear: cappedYears[cappedYears.length - 1],
		};
	}

	if (customPeriods.length > 0 && !customComparePeriodsAlignForYearMonths(anchor, customPeriods, periodSpec)) {
		return null;
	}

	const isQuarterMode = periodSpec.granularity === PERIOD_YOY_GRANULARITY.QUARTER;
	const isFullQuarterAxis = isQuarterMode && periodSpec.indices.length === ALL_QUARTER_INDICES.length;
	const isFullMonthAxis = !isQuarterMode && periodSpec.indices.length === ALL_MONTH_INDICES.length;

	return {
		years: cappedYears,
		months: isQuarterMode ? null : isFullMonthAxis ? ALL_MONTH_INDICES : periodSpec.indices,
		quarters: isQuarterMode ? (isFullQuarterAxis ? ALL_QUARTER_INDICES : periodSpec.indices) : null,
		granularity: periodSpec.granularity,
		anchorYear: cappedYears[cappedYears.length - 1],
	};
};

export const resolveLineCompareMonthlyYears = (filters = {}) => resolveLineCompareMonthlySpec(filters)?.years ?? [];

export const isLineCompareYearMonthsMode = (filters = {}) => Boolean(resolveLineCompareMonthlySpec(filters));

export const pickTrendFields = (filters = {}) => ({
	trendAnchor: filters.trendAnchor ?? null,
	customComparePeriods: filters.customComparePeriods ?? [],
	trendMode: filters.trendMode ?? TREND_MODE.PERIODS,
	trendMonthlyYears: filters.trendMonthlyYears ?? [],
	trendMonthlyYoy: filters.trendMonthlyYoy !== false,
	trendCompareAxisUnit: filters.trendCompareAxisUnit ?? null,
});

export const trendFieldsEqual = (left, right) => JSON.stringify(pickTrendFields(left)) === JSON.stringify(pickTrendFields(right));
